import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './Admin.css';

const Reports = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [exportType, setExportType] = useState('users');

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/analytics', {
        params: { period }
      });
      setAnalytics(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to fetch analytics');
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get(`/admin/export/${exportType}`, {
        params: { format: 'json' }
      });
      
      // Create a download link
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${exportType}_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`${exportType} data exported successfully`);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>📊 Reports & Analytics</h1>
          <p>Platform insights and data exports</p>
        </div>
        <div className="header-actions">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="form-select"
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
            <option value="365">Last Year</option>
          </select>
        </div>
      </div>

      {/* Growth Charts */}
      <div className="reports-section">
        <h2>📈 Growth Trends</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon users">👥</div>
            <div className="stat-info">
              <h3>{analytics?.userGrowth?.length || 0}</h3>
              <p>User Registrations</p>
              <span className="stat-trend">Last {period} days</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon jobs">💼</div>
            <div className="stat-info">
              <h3>{analytics?.jobGrowth?.length || 0}</h3>
              <p>Jobs Posted</p>
              <span className="stat-trend">Last {period} days</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon applications">📝</div>
            <div className="stat-info">
              <h3>{analytics?.applicationGrowth?.length || 0}</h3>
              <p>Applications Submitted</p>
              <span className="stat-trend">Last {period} days</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Companies */}
      <div className="reports-section">
        <h2>🏢 Top Companies</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Company Name</th>
                <th>Total Jobs</th>
              </tr>
            </thead>
            <tbody>
              {analytics?.topCompanies?.length === 0 ? (
                <tr>
                  <td colSpan="3" className="text-center">No data available</td>
                </tr>
              ) : (
                analytics?.topCompanies?.map((company, index) => (
                  <tr key={company._id}>
                    <td>
                      <span className="rank-badge">#{index + 1}</span>
                    </td>
                    <td>{company.name}</td>
                    <td>
                      <span className="badge badge-primary">{company.jobCount}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Jobs */}
      <div className="reports-section">
        <h2>🔥 Top Performing Jobs</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Job Title</th>
                <th>Company</th>
                <th>Applications</th>
                <th>Views</th>
              </tr>
            </thead>
            <tbody>
              {analytics?.topJobs?.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">No data available</td>
                </tr>
              ) : (
                analytics?.topJobs?.map((job, index) => (
                  <tr key={job._id}>
                    <td>
                      <span className="rank-badge">#{index + 1}</span>
                    </td>
                    <td>{job.title}</td>
                    <td>{job.companyId?.name || 'N/A'}</td>
                    <td>
                      <span className="badge badge-success">{job.applications || 0}</span>
                    </td>
                    <td>
                      <span className="badge badge-info">{job.views || 0}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Data Export Section */}
      <div className="reports-section">
        <h2>💾 Export Data</h2>
        <div className="export-section">
          <div className="export-controls">
            <select
              value={exportType}
              onChange={(e) => setExportType(e.target.value)}
              className="form-select"
            >
              <option value="users">Users Data</option>
              <option value="jobs">Jobs Data</option>
              <option value="applications">Applications Data</option>
              <option value="companies">Companies Data</option>
            </select>
            <button className="btn btn-primary" onClick={handleExport}>
              📥 Export as JSON
            </button>
          </div>
          <p className="export-info">
            Export platform data for analysis, backup, or reporting purposes.
          </p>
        </div>
      </div>

      {/* Growth Timeline */}
      {analytics?.userGrowth && analytics.userGrowth.length > 0 && (
        <div className="reports-section">
          <h2>📅 Daily Growth Timeline</h2>
          <div className="timeline-container">
            <div className="timeline-chart">
              {analytics.userGrowth.map((item, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-date">{item._id}</div>
                  <div className="timeline-bar" style={{ height: `${Math.min(item.count * 10, 100)}px` }}>
                    <span className="timeline-count">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
